import { useState } from "react";
import { AlertTriangle, Check, X, Loader2, Info } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

/**
 * WeekReconciliationCard — Iter 372.p3
 *
 * Renders the per-(member, ISO-week) reconciliation from the daily
 * register response. Shows scheduled vs worked, ordinary vs OT gross,
 * shortfall, and the balanced OT (net of same-week short-hours).
 *
 * For weeks where the user's `short_hours_prompt = "ask"` AND there's
 * both a shortfall and gross OT, HR can pick per week:
 *   • Apply balancing — persists a `balance` payroll_adjustments record
 *   • Pay OT gross    — persists a `gross` record
 *   • Clear           — removes any prior decision (revert to policy default)
 *
 * Any weeks whose gross OT breaches the BCEA statutory 10h weekly cap
 * are flagged with a red warning banner.
 */

function fmt(min) {
  if (!min || min <= 0) return "—";
  const h = Math.floor(min / 60);
  const m = Math.round(min - h * 60);
  return `${h}h${String(m).padStart(2, "0")}`;
}

const DECISION_LABEL = {
  balance: { text: "Balanced", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  gross:   { text: "OT gross", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  pending: { text: "Awaiting HR decision", cls: "bg-rose-500/15 text-rose-300 border-rose-500/30" },
};

export default function WeekReconciliationCard({ weeks, isHr, onChanged }) {
  const [busy, setBusy] = useState(null);
  if (!Array.isArray(weeks) || weeks.length === 0) return null;

  // Only render weeks that carry useful info (any OT gross, shortfall,
  // an override, or a cap hit). Skip clean full-standard weeks so the
  // card doesn't get noisy for a normal filter window.
  const meaningful = weeks.filter(
    (w) => w.gross_overtime_min > 0 || w.shortfall_min > 0 || w.override || w.overtime_cap_hit,
  );
  if (meaningful.length === 0) return null;

  const decide = async (w, decision) => {
    setBusy(`${w.user_id}::${w.iso_week}::${decision}`);
    try {
      await api.post("/team/attendance/payroll-adjustments", {
        user_id: w.user_id,
        iso_week: w.iso_week,
        decision, // "balance" | "gross" | "clear"
      });
      toast.success(
        decision === "clear"
          ? `Reset ${w.iso_week} for ${w.user_name || "member"}`
          : `${decision === "balance" ? "Balanced" : "Paid gross"} ${w.iso_week} for ${w.user_name || "member"}`,
      );
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed.");
    } finally {
      setBusy(null);
    }
  };

  // Sort: most recent week first, then user name.
  const sorted = [...meaningful].sort((a, b) => {
    if (b.iso_week !== a.iso_week) return b.iso_week.localeCompare(a.iso_week);
    return (a.user_name || "").localeCompare(b.user_name || "");
  });

  return (
    <div className="hv-card overflow-x-auto" data-testid="week-reconciliation-card">
      <div className="p-3 border-b border-white/5 flex items-center justify-between">
        <h3 className="font-display text-sm font-bold text-white inline-flex items-center gap-1.5">
          <Info className="w-3.5 h-3.5 text-[#a0e6ea]" />
          Week reconciliation
        </h3>
        <span className="text-[10px] text-[#647087]">
          BCEA labour law · balance short-hours against same-week overtime
        </span>
      </div>
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase tracking-wider text-[#647087] border-b border-white/5">
          <tr>
            <th className="text-left p-2">Week</th>
            <th className="text-left p-2">Member</th>
            <th className="text-right p-2">Scheduled</th>
            <th className="text-right p-2">Worked</th>
            <th className="text-right p-2">Ordinary</th>
            <th className="text-right p-2" title="Short-hours: scheduled minus worked ordinary">Short</th>
            <th className="text-right p-2" title="Gross weekday overtime this week">OT gross</th>
            <th className="text-right p-2" title="OT net of same-week short-hours absorption">OT balanced</th>
            <th className="text-left p-2">Status</th>
            {isHr && <th className="text-right p-2">Action</th>}
          </tr>
        </thead>
        <tbody>
          {sorted.map((w) => {
            const key = `${w.user_id}::${w.iso_week}`;
            const label = DECISION_LABEL[w.decision] || DECISION_LABEL.pending;
            const canBalance = w.shortfall_min > 0 && w.gross_overtime_min > 0;
            return (
              <tr key={key} className="border-b border-white/[0.04] hover:bg-white/[0.02]" data-testid={`week-recon-row-${key}`}>
                <td className="p-2 whitespace-nowrap font-mono text-[10px] text-white/70">
                  {w.iso_week}
                  <div className="text-[9px] text-[#647087]">{w.start} → {w.end}</div>
                </td>
                <td className="p-2 truncate max-w-[160px] text-white/90">{w.user_name || w.user_id.slice(0, 8)}</td>
                <td className="p-2 text-right tabular-nums text-[#a0aabf]">{fmt(w.scheduled_min)}</td>
                <td className="p-2 text-right tabular-nums">{fmt(w.worked_min)}</td>
                <td className="p-2 text-right tabular-nums">{fmt(w.ordinary_min)}</td>
                <td className="p-2 text-right tabular-nums text-amber-300">{fmt(w.shortfall_min)}</td>
                <td className="p-2 text-right tabular-nums text-rose-300">{fmt(w.gross_overtime_min)}</td>
                <td className="p-2 text-right tabular-nums font-bold text-[#1FB6C1]">{fmt(w.balanced_overtime_min)}</td>
                <td className="p-2">
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${label.cls}`}
                    data-testid={`week-recon-status-${key}`}
                  >
                    {w.decision === "balance" ? <Check className="w-3 h-3" /> : null}
                    {w.decision === "pending" ? <AlertTriangle className="w-3 h-3" /> : null}
                    {label.text}
                  </span>
                  {w.overtime_cap_hit && (
                    <div className="text-[9px] text-rose-300 mt-0.5" data-testid={`week-recon-cap-warn-${key}`}>
                      ⚠ BCEA 10h/week OT cap breached
                    </div>
                  )}
                </td>
                {isHr && (
                  <td className="p-2 text-right whitespace-nowrap">
                    {canBalance && w.decision !== "balance" && (
                      <button
                        type="button"
                        onClick={() => decide(w, "balance")}
                        disabled={!!busy}
                        className="hv-btn hv-btn-ghost text-[10px] mr-1 disabled:opacity-50"
                        data-testid={`week-recon-apply-${key}`}
                      >
                        {busy === `${key}::balance` ? <Loader2 className="w-3 h-3 animate-spin" /> : "Balance"}
                      </button>
                    )}
                    {w.gross_overtime_min > 0 && w.decision !== "gross" && (
                      <button
                        type="button"
                        onClick={() => decide(w, "gross")}
                        disabled={!!busy}
                        className="hv-btn hv-btn-ghost text-[10px] mr-1 disabled:opacity-50"
                        data-testid={`week-recon-gross-${key}`}
                      >
                        {busy === `${key}::gross` ? <Loader2 className="w-3 h-3 animate-spin" /> : "Pay gross"}
                      </button>
                    )}
                    {w.override && (
                      <button
                        type="button"
                        onClick={() => decide(w, "clear")}
                        disabled={!!busy}
                        className="hv-btn hv-btn-ghost text-[10px] text-[#647087] disabled:opacity-50"
                        title="Revert to policy default"
                        data-testid={`week-recon-clear-${key}`}
                      >
                        {busy === `${key}::clear` ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
